import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sparkles, ArrowLeft, Plus, Loader2, Play, Eye, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const MiniLessons = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [filter, setFilter] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newLesson, setNewLesson] = useState({
    title: '',
    description: '',
    video_url: '',
    category: 'basics'
  });

  useEffect(() => {
    loadLessons();
  }, [filter]);

  const loadLessons = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/lessons`, {
        params: { category: filter },
        withCredentials: true
      });
      setLessons(response.data.lessons || []);
    } catch (error) {
      console.error('Load error:', error);
    }
  };

  const handleUpload = async () => {
    if (!newLesson.title.trim() || !newLesson.description.trim() || !newLesson.video_url.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsUploading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/lessons/upload`, null, {
        params: {
          title: newLesson.title,
          description: newLesson.description,
          video_url: newLesson.video_url,
          category: newLesson.category
        },
        withCredentials: true
      });
      toast.success('Lesson uploaded successfully!');
      setIsDialogOpen(false);
      setNewLesson({ title: '', description: '', video_url: '', category: 'basics' });
      loadLessons();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload lesson');
    } finally {
      setIsUploading(false);
    }
  };

  const getVideoEmbedUrl = (url) => {
    // Convert various video URLs to embed format
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  return (
    <div className="min-h-screen">
      <nav className="px-6 py-4 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Sparkles className="h-7 w-7 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">Mini Lessons</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-4xl font-bold text-foreground">Learning <span className="text-primary">Tutorials</span></h2>
            <p className="text-lg text-muted-foreground mt-2">Watch and learn prompt engineering skills</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-white shadow-md hover:shadow-lg font-semibold">
                <Plus className="mr-2 h-5 w-5" /> Upload Video Tutorial
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Upload Video Tutorial</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Lesson Title</label>
                  <Input 
                    value={newLesson.title}
                    onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                    placeholder="e.g., How to Improve Face Details in AI Images"
                    className="border border-border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <Textarea 
                    value={newLesson.description}
                    onChange={(e) => setNewLesson({...newLesson, description: e.target.value})}
                    placeholder="Brief description of what this lesson teaches..."
                    className="min-h-[100px] border border-border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Video URL</label>
                  <Input 
                    value={newLesson.video_url}
                    onChange={(e) => setNewLesson({...newLesson, video_url: e.target.value})}
                    placeholder="YouTube URL or video embed link"
                    className="border border-border"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Paste YouTube link or direct video URL</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Category</label>
                  <Select value={newLesson.category} onValueChange={(v) => setNewLesson({...newLesson, category: v})}>
                    <SelectTrigger className="border border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basics">Basics</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="image">Image Generation</SelectItem>
                      <SelectItem value="video">Video Generation</SelectItem>
                      <SelectItem value="tips">Tips & Tricks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="w-full bg-primary text-white shadow-md hover:shadow-lg font-semibold py-6"
                >
                  {isUploading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Uploading...</>
                  ) : (
                    <><Plus className="mr-2 h-5 w-5" /> Upload Tutorial</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-3 mb-8 mt-8 flex-wrap">
          <Button onClick={() => setFilter(null)} variant={!filter ? 'default' : 'outline'} className="border border-border">All</Button>
          <Button onClick={() => setFilter('basics')} variant={filter === 'basics' ? 'default' : 'outline'} className="border border-border">Basics</Button>
          <Button onClick={() => setFilter('advanced')} variant={filter === 'advanced' ? 'default' : 'outline'} className="border border-border">Advanced</Button>
          <Button onClick={() => setFilter('image')} variant={filter === 'image' ? 'default' : 'outline'} className="border border-border">Image</Button>
          <Button onClick={() => setFilter('video')} variant={filter === 'video' ? 'default' : 'outline'} className="border border-border">Video</Button>
          <Button onClick={() => setFilter('tips')} variant={filter === 'tips' ? 'default' : 'outline'} className="border border-border">Tips</Button>
        </div>

        {lessons.length === 0 ? (
          <Card className="p-12 bg-white border border-border card-shadow-lg text-center">
            <Play className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg text-muted-foreground">No tutorials yet. Upload the first one!</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons.map((lesson) => (
              <Card key={lesson.lesson_id} className="bg-white border border-border card-shadow-lg hover:shadow-xl transition-all overflow-hidden">
                <div className="aspect-video bg-gray-200 relative">
                  {lesson.video_url && (
                    <iframe
                      src={getVideoEmbedUrl(lesson.video_url)}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={lesson.title}
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="mb-3">
                    <span className="bg-primary/10 px-3 py-1 rounded-lg text-xs font-semibold border border-primary/20 text-primary">
                      {lesson.category}
                    </span>
                  </div>
                  <h3 className="font-semibold mb-2 text-foreground text-lg">{lesson.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{lesson.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {lesson.views || 0}
                    </div>
                    <div className="flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3" />
                      {lesson.likes || 0}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniLessons;
